import { ASSET_REDOWNLOAD_ATTEMPTS } from '../constants.js';
import type { Sha256Hasher } from '../hash/sha256.js';
import type { Logger } from '../log.js';
import type { ManifestItem } from '../manifest/types.js';
import type { Downloader } from './downloader.js';
import type { AssetStore, CachedAsset } from './store.js';

/**
 * Cache manager, technical-design §11.5:
 * - LRU eviction against `cache_max_bytes`, but items in the current manifest are
 *   pinned and never evicted (acceptance criterion 6);
 * - sha256 verified before first play and after any partial download;
 * - range-resume downloads with backoff (see downloader.ts);
 * - the next item prefetched during playback;
 * - the last known-good manifest's assets stay pinned until the new manifest is
 *   fully cached and verified, so there is always a rollback target.
 */

export type EnsureResult =
  | { readonly ok: true; readonly source: 'cache' | 'download' }
  | { readonly ok: false; readonly reason: string };

export type ReadResult =
  | { readonly ok: true; readonly asset: CachedAsset }
  | { readonly ok: false; readonly reason: 'missing' | 'sha256_mismatch' };

export interface CacheManager {
  /** `settings.cache_max_bytes` once a manifest is in hand. */
  setCeiling(bytes: number): void;
  /** Digests that must survive eviction: current loop plus the rollback target. */
  pin(digests: Iterable<string>): void;
  pinned(): readonly string[];
  ensure(item: ManifestItem): Promise<EnsureResult>;
  ensureAll(items: readonly ManifestItem[]): Promise<{
    readonly ok: boolean;
    readonly failures: readonly { item_id: string; reason: string }[];
  }>;
  /** Verifies the digest before handing bytes to the renderer. */
  readForPlay(item: ManifestItem): Promise<ReadResult>;
  /** Best-effort warm of the next item; never awaited by the loop. */
  prefetch(item: ManifestItem): void;
  evict(): Promise<number>;
  bytesUsed(): Promise<number>;
}

export interface CacheManagerOptions {
  readonly store: AssetStore;
  readonly downloader: Downloader;
  readonly hasher: Sha256Hasher;
  readonly logger: Logger;
  readonly now: () => number;
  readonly ceilingBytes: number;
}

export function createCacheManager(options: CacheManagerOptions): CacheManager {
  let ceiling = options.ceilingBytes;
  let pinnedDigests = new Set<string>();
  let lastKnownBytes = 0;
  const verified = new Set<string>();
  const inFlight = new Map<string, Promise<EnsureResult>>();

  /**
   * The disk is allowed to fail. A stick runs out of storage, the shell force-closes
   * the WebView's database, a file goes bad — IndexedDB reports all of it as a
   * rejected request, and none of it is a reason to stop playing. A rejection that
   * escapes this module escapes `runLoop` too, and a dead loop leaves whatever was on
   * screen frozen there until someone power-cycles the TV (invariant 9). So every call
   * into the store comes through here, and a failure becomes a value the state machine
   * already knows how to handle.
   */
  const guard = async <T>(operation: string, fallback: T, work: () => Promise<T>): Promise<T> => {
    try {
      return await work();
    } catch (error) {
      options.logger.error('cache.store_error', {
        operation,
        reason: error instanceof Error ? error.message : String(error),
      });
      return fallback;
    }
  };

  const fetchAndStore = async (item: ManifestItem): Promise<EnsureResult> => {
    const partial = await guard('readPartial', null, () =>
      options.store.readPartial(item.asset_sha256),
    );
    const result = await options.downloader.download(item.asset_url, {
      resumeFrom: partial,
      onPartial: async (bytes) => {
        // A partial that cannot be written costs a restart of the download, not the loop.
        await guard('writePartial', undefined, () =>
          options.store.writePartial(item.asset_sha256, bytes),
        );
      },
    });
    if (!result.ok) return { ok: false, reason: result.reason };

    let digest: string;
    try {
      digest = await options.hasher(result.bytes);
    } catch (error) {
      // An unusable hasher must not look like a bad asset: verification is an
      // invariant, so the item is skipped, but nothing is deleted on its word.
      options.logger.error('asset.hash_failed', {
        item_id: item.item_id,
        reason: error instanceof Error ? error.message : String(error),
      });
      return { ok: false, reason: 'hash_failed' };
    }

    if (digest !== item.asset_sha256) {
      await guard('clearPartial', undefined, () => options.store.clearPartial(item.asset_sha256));
      options.logger.error('asset.sha256_mismatch', {
        item_id: item.item_id,
        expected: item.asset_sha256,
        actual: digest,
        stage: 'download',
      });
      return { ok: false, reason: 'sha256_mismatch' };
    }

    const stored = await guard('put', false, async () => {
      await options.store.put({
        sha256: item.asset_sha256,
        bytes: result.bytes,
        media_type: item.media_type,
        at_ms: options.now(),
      });
      return true;
    });
    if (!stored) return { ok: false, reason: 'store_write_failed' };

    await guard('clearPartial', undefined, () => options.store.clearPartial(item.asset_sha256));
    verified.add(item.asset_sha256);
    await evictUnpinned();
    return { ok: true, source: 'download' };
  };

  const evictUnpinned = async (): Promise<number> => {
    const metas = [...(await guard('list', [], () => options.store.list()))];
    let total = metas.reduce((sum, meta) => sum + meta.byte_length, 0);
    if (total <= ceiling) return 0;

    const candidates = metas
      .filter((meta) => !pinnedDigests.has(meta.sha256))
      .sort((a, b) => a.last_access_ms - b.last_access_ms);

    let evicted = 0;
    for (const candidate of candidates) {
      if (total <= ceiling) break;
      const removed = await guard('delete', false, async () => {
        await options.store.delete(candidate.sha256);
        return true;
      });
      if (!removed) continue;
      verified.delete(candidate.sha256);
      total -= candidate.byte_length;
      evicted += 1;
      options.logger.info('cache.evicted', {
        sha256: candidate.sha256,
        byte_length: candidate.byte_length,
      });
    }
    if (total > ceiling) {
      // Every remaining asset is pinned: the current loop is larger than the
      // ceiling. Playback matters more than the ceiling, so this is a warning,
      // not a deletion.
      options.logger.warn('cache.over_ceiling_all_pinned', { total_bytes: total, ceiling });
    }
    return evicted;
  };

  const ensure = async (item: ManifestItem): Promise<EnsureResult> => {
    if (await guard('has', false, () => options.store.has(item.asset_sha256))) {
      await guard('touch', undefined, () => options.store.touch(item.asset_sha256, options.now()));
      return { ok: true, source: 'cache' };
    }

    const existing = inFlight.get(item.asset_sha256);
    if (existing !== undefined) return existing;

    const attempt = (async (): Promise<EnsureResult> => {
      let last: EnsureResult = { ok: false, reason: 'not_attempted' };
      for (let tries = 0; tries <= ASSET_REDOWNLOAD_ATTEMPTS; tries += 1) {
        last = await fetchAndStore(item);
        if (last.ok) return last;
      }
      return last;
    })().finally(() => {
      inFlight.delete(item.asset_sha256);
    });

    inFlight.set(item.asset_sha256, attempt);
    return attempt;
  };

  return {
    setCeiling: (bytes) => {
      ceiling = bytes;
    },

    pin: (digests) => {
      pinnedDigests = new Set(digests);
    },

    pinned: () => [...pinnedDigests],

    ensure,

    ensureAll: async (items) => {
      const failures: { item_id: string; reason: string }[] = [];
      for (const item of items) {
        const result = await ensure(item);
        if (!result.ok) failures.push({ item_id: item.item_id, reason: result.reason });
      }
      return { ok: failures.length === 0, failures };
    },

    readForPlay: async (item) => {
      // A disk that cannot be read is an item that cannot be played: the caller skips
      // it, re-downloads in the background and moves on, which is what it already does
      // for an asset that is simply absent (§11.7).
      const asset = await guard('get', null, () => options.store.get(item.asset_sha256));
      if (asset === null) return { ok: false, reason: 'missing' };

      if (!verified.has(item.asset_sha256)) {
        let digest: string;
        try {
          digest = await options.hasher(asset.bytes);
        } catch (error) {
          options.logger.error('asset.hash_failed', {
            item_id: item.item_id,
            reason: error instanceof Error ? error.message : String(error),
            stage: 'pre_play',
          });
          return { ok: false, reason: 'missing' };
        }
        if (digest !== item.asset_sha256) {
          await guard('delete', undefined, () => options.store.delete(item.asset_sha256));
          options.logger.error('asset.sha256_mismatch', {
            item_id: item.item_id,
            expected: item.asset_sha256,
            actual: digest,
            stage: 'pre_play',
          });
          return { ok: false, reason: 'sha256_mismatch' };
        }
        verified.add(item.asset_sha256);
      }

      // Verified bytes in hand. Failing to record the access is an LRU inaccuracy, not
      // a reason to hold the frame back.
      await guard('touch', undefined, () => options.store.touch(item.asset_sha256, options.now()));
      return { ok: true, asset };
    },

    prefetch: (item) => {
      void ensure(item).then(
        (result) => {
          if (!result.ok) {
            options.logger.warn('cache.prefetch_failed', {
              item_id: item.item_id,
              reason: result.reason,
            });
          }
        },
        (error: unknown) => {
          // `ensure` is not supposed to reject. If it ever does, it must not surface as
          // an unhandled rejection in a WebView nobody is looking at.
          options.logger.error('cache.prefetch_error', {
            item_id: item.item_id,
            reason: error instanceof Error ? error.message : String(error),
          });
        },
      );
    },

    evict: evictUnpinned,

    /**
     * Feeds the §6.6 heartbeat's `cache_bytes`. A rejection here would take the whole
     * heartbeat with it, and a screen that stops heartbeating reads as dark on the
     * fleet dashboard — so an unreadable disk reports the last figure it did read,
     * and the failure travels in `last_error` instead.
     */
    bytesUsed: async () => {
      lastKnownBytes = await guard('totalBytes', lastKnownBytes, () => options.store.totalBytes());
      return lastKnownBytes;
    },
  };
}
